import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';
import { useAuth } from '@/contexts/AuthContext';

export interface MembershipStatus {
  is_active_member: boolean;
  membership_tier: string | null;
  member_since_year: number | null;
  member_since_date: string | null;
  continuous_months: number | null;
  // Grace period info
  in_grace_period: boolean;
  grace_period_end_date: string | null;
  days_until_membership_loss: number | null;
}

/**
 * Hook to fetch the current user's membership status
 */
export const useMembershipStatus = () => {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery<MembershipStatus>({
    queryKey: ['membershipStatus', user?.id],
    queryFn: async () => {
      const response = await apiClient.get<MembershipStatus>('/auth/me/membership');
      return response;
    },
    enabled: !!user,
    retry: 1,
    retryDelay: 1000,
    staleTime: 60 * 1000, // Cache for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  return {
    membershipStatus: data,
    isLoading,
    error,
    refetch,
    isActiveMember: data?.is_active_member ?? false,
    inGracePeriod: data?.in_grace_period ?? false,
    daysUntilMembershipLoss: data?.days_until_membership_loss ?? null,
  };
};
